import { env } from '../env.js';

// Singleton WebSocket client for svc-finance's `/api/v1/finance-stream/ws`.
// Lives outside React so connection lifetime is decoupled from component
// renders — navigation, StrictMode double-invoke, and unstable hook deps no
// longer cause a fresh socket per re-render. The connection only opens once
// per access token and only closes on logout (`stop()`).
//
// React surface: `useFinanceStream` subscribes/unsubscribes via `subscribe`.

export interface FinanceStreamEvent {
  event:
    | 'connected'
    | 'deposit.created'
    | 'deposit.updated'
    | 'withdraw.created'
    | 'withdraw.updated'
    | 'bank_account.created'
    | 'bank_account.updated';
  id?: string;
  status?: string;
  agentId?: string;
  memberId?: string;
  amount?: string;
  username?: string;
  bankCode?: string;
  accountNumber?: string;
  accountName?: string;
  verified?: boolean;
  createdAt?: string;
}

/**
 * What the operator's live/offline indicator is driven by.
 *
 *   live     — the socket is open; the queue updates the moment a request lands
 *   offline  — no socket right now (connecting, dropped, retrying); the lists
 *              fall back to polling, which is the only reason a dead socket is
 *              survivable at all
 *   denied   — the server refused this token for good (`unauthorized` /
 *              `forbidden` / `token-expired`). Retrying it is a loop, so we stop
 *              and say so; a fresh token via `start()` clears it.
 */
export type FinanceStreamStatus = 'live' | 'offline' | 'denied';

export interface FinanceStreamState {
  status: FinanceStreamStatus;
  /** The server's own word for a refusal, verbatim. `null` when there is none. */
  reason: string | null;
}

/**
 * Refusals that will not change while this token is the token.
 *
 * The server sends one of these and closes. Reconnecting re-runs the identical
 * decision, so the old client reopened a socket it could never keep — every
 * three seconds, then every minute, forever, with nothing on screen to say the
 * queue had stopped updating (#292).
 */
const PERMANENT_REFUSALS = new Set(['unauthorized', 'forbidden', 'token-expired']);

type Listener = (event: FinanceStreamEvent) => void;
type ErrorListener = () => void;
type StateListener = (state: FinanceStreamState) => void;

const FINANCE_WS_BASE = env.VITE_FINANCE_WS_BASE;

/**
 * Reconnect delay: doubles per consecutive failure, capped, reset on a socket
 * that actually opens.
 *
 * A fixed delay assumes the endpoint is momentarily down. This one can be
 * unreachable by construction — the WS path has to be routed around the
 * gateway, whose fetch-based proxy cannot carry an upgrade — and a flat 3s
 * retry against that opened a doomed socket twenty times a minute for as long
 * as the tab stayed open. The first retry is still fast, so a real blip
 * recovers in seconds; a structurally-dead endpoint settles at one attempt a
 * minute instead of twenty.
 */
const RECONNECT_BASE_MS = 3000;
const RECONNECT_MAX_MS = 60_000;

export interface FinanceStreamDeps {
  /** Test seam — defaults to `WebSocket`. */
  wsFactory?: (url: string) => WebSocket;
  /** Test seam — defaults to setTimeout/clearTimeout. */
  timers?: {
    setTimeout: typeof setTimeout;
    clearTimeout: typeof clearTimeout;
  };
}

export class FinanceStreamClient {
  private ws: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private listeners = new Set<Listener>();
  private errorListeners = new Set<ErrorListener>();
  private stateListeners = new Set<StateListener>();
  // Replaced wholesale, never mutated: `useSyncExternalStore` compares the
  // snapshot by identity, so an in-place edit would never re-render.
  private state: FinanceStreamState = { status: 'offline', reason: null };
  private currentToken: string | null = null;
  private intentionalClose = false;
  /** Set by a permanent refusal; suppresses reconnects until the token rotates. */
  private denied = false;
  // The refusal frame that arrived on THIS socket, consumed by its own close.
  // A reason must never outlive the attempt that produced it: an operator whose
  // permissions were fixed would otherwise keep reading "no permission" on an
  // offline state that has nothing to do with permissions.
  private pendingRefusal: string | null = null;
  /** Consecutive failed connects; drives the backoff, cleared by a real open. */
  private attempt = 0;
  private readonly wsFactory: (url: string) => WebSocket;
  private readonly setT: typeof setTimeout;
  private readonly clearT: typeof clearTimeout;

  constructor(deps: FinanceStreamDeps = {}) {
    this.wsFactory = deps.wsFactory ?? ((url) => new WebSocket(url));
    // Bound: browsers throw "Illegal invocation" when window.setTimeout is
    // called with a foreign `this` (here, the client instance via this.setT()) —
    // unbound defaults silently broke the reconnect timer.
    this.setT = deps.timers?.setTimeout ?? setTimeout.bind(globalThis);
    this.clearT = deps.timers?.clearTimeout ?? clearTimeout.bind(globalThis);
  }

  // Connect (or rotate) for the given access token. Idempotent: calling with
  // the same token while connected is a no-op.
  start(accessToken: string): void {
    // `denied` counts as "already handled for this token" — without it a
    // re-render would reopen the socket the server just refused.
    if (this.currentToken === accessToken && (this.ws || this.denied)) return;
    if (this.ws) this.teardown();
    this.currentToken = accessToken;
    this.intentionalClose = false;
    this.denied = false;
    this.pendingRefusal = null;
    this.attempt = 0;
    this.setState('offline', null);
    this.connect();
  }

  // Close permanently. Cancels any pending reconnect; safe to call when idle.
  stop(): void {
    this.intentionalClose = true;
    this.currentToken = null;
    this.denied = false;
    this.pendingRefusal = null;
    this.teardown();
    this.setState('offline', null);
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  onError(fn: ErrorListener): () => void {
    this.errorListeners.add(fn);
    return () => {
      this.errorListeners.delete(fn);
    };
  }

  /** Subscribe to connection-state changes. Fires only when the state changes. */
  onState(fn: StateListener): () => void {
    this.stateListeners.add(fn);
    return () => {
      this.stateListeners.delete(fn);
    };
  }

  getState(): FinanceStreamState {
    return this.state;
  }

  // Diagnostics — used by tests and dev tooling.
  isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
  listenerCount(): number {
    return this.listeners.size;
  }

  private setState(status: FinanceStreamStatus, reason: string | null): void {
    if (this.state.status === status && this.state.reason === reason) return;
    this.state = { status, reason };
    for (const fn of this.stateListeners) fn(this.state);
  }

  private teardown(): void {
    if (this.reconnectTimer) {
      this.clearT(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      const ws = this.ws;
      this.ws = null;
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    }
  }

  private connect(): void {
    if (!this.currentToken) return;
    const url =
      `${FINANCE_WS_BASE}/api/v1/finance-stream/ws` +
      `?access_token=${encodeURIComponent(this.currentToken)}`;
    const ws = this.wsFactory(url);
    this.ws = ws;

    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.attempt = 0;
      this.pendingRefusal = null;
      this.setState('live', null);
    };

    ws.onmessage = (e: MessageEvent) => {
      try {
        const data = JSON.parse(String(e.data)) as FinanceStreamEvent & { error?: unknown };
        // A refusal, not an event. The server sends one and closes; it is the
        // only place the client learns WHY its queue stopped updating, and it
        // must never reach the event listeners as if it were a deposit.
        if (typeof data.error === 'string') {
          const permanent = PERMANENT_REFUSALS.has(data.error);
          if (permanent) this.denied = true;
          this.pendingRefusal = data.error;
          this.setState(permanent ? 'denied' : 'offline', data.error);
          return;
        }
        for (const fn of this.listeners) fn(data);
      } catch {
        /* ignore malformed */
      }
    };

    ws.onclose = () => {
      if (this.ws !== ws) return; // superseded by start()/stop()
      this.ws = null;
      if (this.intentionalClose) return;
      // A refusal this token cannot survive: stay down, keep the reason on
      // screen, and let the lists' polling fallback carry the queue.
      if (this.denied) return;
      const reason = this.pendingRefusal;
      this.pendingRefusal = null;
      this.setState('offline', reason);
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** this.attempt, RECONNECT_MAX_MS);
      this.attempt += 1;
      this.reconnectTimer = this.setT(() => this.connect(), delay);
    };

    ws.onerror = () => {
      for (const fn of this.errorListeners) fn();
    };
  }
}

export const financeStream = new FinanceStreamClient();
